import { supportsIbottaOffers } from "../config/retailers";
export type SortType =
  | "bestDeal"
  | "storePrice"
  | "msrp"
  | "inventory"
  | "discountAmount"
  | "discountPercentage"
  | "openBoxDiscountPercentage"
  | "lastUpdated"
  | "lastSeen"
  | "firstAppeared"
  | string;

export type StatusValue =
  | "all"
  | "new"
  | "price_drop"
  | "open_box"
  | "stock_update"
  | "with_location"
  | "with_options"
  | "saved"
  | "pickup_available"
  | "pickup_unavailable"
  | "on_sale"
  | "sold_by_walmart"
  | "sold_by_pro_seller"
  | "lowest"
  | "cashback"
  | "ibotta"
  | "bogo"
  | "mix_match"
  | "coupon";

export type StatusCounts = {
  all: number;
  new: number;
  priceDrop: number;
  openBox?: number;
  stockUpdate: number;
  withLocation: number;
  withOptions: number;
  saved: number;
  pickupAvailable?: number;
    pickupUnavailable?: number;
    onSale?: number;
    soldByWalmart?: number;
    soldByProSeller?: number;
    lowest?: number;
    cashback?: number;
    /** Products with at least one live Ibotta offer (retailers in IBOTTA_RETAILERS). */
    ibotta?: number;
    bogo?: number;
    mixMatch?: number;
    coupon?: number;
};

export type StatusOption = {
  value: StatusValue;
  label: string;
  count?: number;
};

export const getSortLabel = (sortType: SortType, t?: (key: string) => string) => {
  if (t) {
    switch (sortType) {
      case "bestDeal": return t("products.sortBestDeal");
      case "storePrice": return t("products.sortLabelPrice");
      case "msrp": return t("products.sortLabelMsrp");
      case "inventory": return t("products.sortLabelInventory");
      case "discountAmount": return t("products.sortLabelDiscountDollar");
      case "discountPercentage": return t("products.sortLabelDiscount");
      case "cashbackAmount": return t("products.sortLabelCashback");
      case "openBoxDiscountPercentage": return t("products.sortLabelOpenBoxDiscount");
      case "lastSeen": return t("products.sortLabelChanged");
      case "lastUpdated": return t("products.sortLabelUpdated");
      case "firstAppeared": return t("products.sortLabelNewest");
      default: return t("products.sortLabelDefault");
    }
  }
  switch (sortType) {
    case "bestDeal": return "Best Deal";
    case "storePrice": return "Price";
    case "msrp": return "MSRP";
    case "inventory": return "Inventory";
    case "discountAmount": return "Discount $";
    case "discountPercentage": return "Discount";
    case "cashbackAmount": return "Cash Back";
    case "openBoxDiscountPercentage": return "Open Box Discount";
    case "lastSeen": return "Changed";
    case "lastUpdated": return "Updated";
    case "firstAppeared": return "Newest";
    default: return "Sort";
  }
};

export const getSortIconKey = (sortType: SortType) => {
  switch (sortType) {
    case "storePrice":
      return "price";
    case "msrp":
      return "price";
    case "inventory":
      return "inventory";
    case "discountAmount":
      return "discount";
    case "discountPercentage":
      return "discount";
    case "cashbackAmount":
      return "discount";
    case "openBoxDiscountPercentage":
      return "discount";
    case "lastSeen":
      return "updated";
    case "lastUpdated":
      return "seen";
    case "firstAppeared":
      return "newest";
    default:
      return "default";
  }
};

export const buildStatusOptions = (input: {
  retailer: string;
  isOnlineRetailer: boolean;
  counts?: StatusCounts;
  t?: (key: string) => string;
}) => {
  const { retailer, isOnlineRetailer, counts } = input;
  const l = (key: string, fallback: string) => {
    const translated = input.t?.(key);
    return translated && translated !== key ? translated : fallback;
  };
  if (retailer === "woot") {
    return [
      { value: "all", label: l("products.statusAll", "All"), count: counts?.all },
      { value: "saved", label: l("products.statusSaved", "Saved"), count: counts?.saved },
      { value: "new", label: l("products.statusNew", "New"), count: counts?.new },
      { value: "price_drop", label: l("products.statusPriceDrop", "Price Drop"), count: counts?.priceDrop },
      { value: "lowest", label: l("products.statusLowest", "Lowest"), count: counts?.lowest },
    ] as StatusOption[];
  }
  if (isOnlineRetailer) {
    const options: StatusOption[] = [
      { value: "all", label: l("products.statusAll", "All"), count: counts?.all },
      { value: "saved", label: l("products.statusSaved", "Saved"), count: counts?.saved },
      { value: "new", label: l("products.statusNew", "New"), count: counts?.new },
      { value: "price_drop", label: l("products.statusPriceDrop", "Price Drop"), count: counts?.priceDrop },
      { value: "lowest", label: l("products.statusLowest", "Lowest"), count: counts?.lowest },
    ];
    if (retailer === "walmart_online" || (retailer === "walmart" && isOnlineRetailer)) {
      options.push(
        { value: "sold_by_walmart", label: l("products.statusSoldByWalmart", "Sold by Walmart"), count: counts?.soldByWalmart },
        { value: "sold_by_pro_seller", label: l("products.statusSoldByProSeller", "Sold by Pro Seller"), count: counts?.soldByProSeller },
      );
    }
    return options;
  }
  if (retailer === "walmart") {
    return [
      { value: "all", label: l("products.statusAll", "All"), count: counts?.all },
      { value: "saved", label: l("products.statusSaved", "Saved"), count: counts?.saved },
      { value: "new", label: l("products.statusNew", "New"), count: counts?.new },
      { value: "cashback", label: l("products.statusCashback", "Cash Back"), count: counts?.cashback },
      { value: "ibotta", label: l("products.statusIbotta", "Ibotta"), count: counts?.ibotta },
      { value: "price_drop", label: l("products.statusPriceDrop", "Price Drop"), count: counts?.priceDrop },
      { value: "lowest", label: l("products.statusLowest", "Lowest"), count: counts?.lowest },
      { value: "stock_update", label: l("products.statusStock", "Stock"), count: counts?.stockUpdate },
      { value: "with_location", label: l("products.statusAisle", "Aisle"), count: counts?.withLocation },
      { value: "with_options", label: l("products.statusWithOptions", "With Options"), count: counts?.withOptions },
      { value: "pickup_available", label: l("products.statusPickupYes", "Pickup ✓"), count: counts?.pickupAvailable },
      { value: "pickup_unavailable", label: l("products.statusPickupNo", "Pickup ✗"), count: counts?.pickupUnavailable },
    ] as StatusOption[];
  }
  if (retailer === "dollar_general") {
    return [
      { value: "all", label: l("products.statusAll", "All"), count: counts?.all },
      { value: "coupon", label: l("products.statusCoupon", "Coupon"), count: counts?.coupon },
      { value: "saved", label: l("products.statusSaved", "Saved"), count: counts?.saved },
      { value: "new", label: l("products.statusNew", "New"), count: counts?.new },
      { value: "cashback", label: l("products.statusCashback", "Cash Back"), count: counts?.cashback },
      { value: "ibotta", label: l("products.statusIbotta", "Ibotta"), count: counts?.ibotta },
      { value: "bogo", label: l("products.statusBogo", "BOGO"), count: counts?.bogo },
      { value: "mix_match", label: l("products.statusMixMatch", "Mix & Match"), count: counts?.mixMatch },
      { value: "price_drop", label: l("products.statusPriceDrop", "Price Drop"), count: counts?.priceDrop },
      { value: "lowest", label: l("products.statusLowest", "Lowest"), count: counts?.lowest },
      { value: "stock_update", label: l("products.statusStockUpdate", "Stock Update"), count: counts?.stockUpdate },
    ] as StatusOption[];
  }
  if (retailer === "target") {
    return [
      { value: "all", label: l("products.statusAll", "All"), count: counts?.all },
      { value: "on_sale", label: l("products.statusOnSale", "On Sale"), count: counts?.onSale },
      { value: "saved", label: l("products.statusSaved", "Saved"), count: counts?.saved },
      { value: "new", label: l("products.statusNew", "New"), count: counts?.new },
      { value: "ibotta", label: l("products.statusIbotta", "Ibotta"), count: counts?.ibotta },
      { value: "price_drop", label: l("products.statusPriceDrop", "Price Drop"), count: counts?.priceDrop },
      { value: "lowest", label: l("products.statusLowest", "Lowest"), count: counts?.lowest },
      { value: "stock_update", label: l("products.statusStock", "Stock"), count: counts?.stockUpdate },
      { value: "with_location", label: l("products.statusAisle", "Aisle"), count: counts?.withLocation },
      { value: "with_options", label: l("products.statusWithOptions", "With Options"), count: counts?.withOptions },
    ] as StatusOption[];
  }
  if (retailer === "bestbuy") {
    return [
      { value: "all", label: l("products.statusAll", "All"), count: counts?.all },
      { value: "saved", label: l("products.statusSaved", "Saved"), count: counts?.saved },
      { value: "new", label: l("products.statusNew", "New"), count: counts?.new },
      { value: "price_drop", label: l("products.statusPriceDrop", "Price Drop"), count: counts?.priceDrop },
      { value: "lowest", label: l("products.statusLowest", "Lowest"), count: counts?.lowest },
      { value: "open_box", label: l("products.statusOpenBox", "Open Box"), count: counts?.openBox },
    ] as StatusOption[];
  }
  return [
    { value: "all", label: l("products.statusAll", "All"), count: counts?.all },
    { value: "saved", label: l("products.statusSaved", "Saved"), count: counts?.saved },
    { value: "new", label: l("products.statusNew", "New"), count: counts?.new },
    ...(retailer === "dollar_general"
      ? [
          { value: "cashback" as const, label: l("products.statusCashback", "Cash Back"), count: counts?.cashback },
          { value: "bogo" as const, label: l("products.statusBogo", "BOGO"), count: counts?.bogo },
          { value: "mix_match" as const, label: l("products.statusMixMatch", "Mix & Match"), count: counts?.mixMatch },
        ]
      : []),
    ...(supportsIbottaOffers(retailer)
      ? [{ value: "ibotta" as const, label: l("products.statusIbotta", "Ibotta"), count: counts?.ibotta }]
      : []),
    { value: "price_drop", label: l("products.statusPriceDrop", "Price Drop"), count: counts?.priceDrop },
    { value: "lowest", label: l("products.statusLowest", "Lowest"), count: counts?.lowest },
    { value: "stock_update", label: l("products.statusStock", "Stock"), count: counts?.stockUpdate },
    { value: "with_location", label: l("products.statusAisle", "Aisle"), count: counts?.withLocation },
    { value: "with_options", label: l("products.statusWithOptions", "With Options"), count: counts?.withOptions },
  ] as StatusOption[];
};

const formatStatusCount = (n: number) => n.toLocaleString();

export const getStatusLabel = (
  options: StatusOption[],
  current: StatusValue
) => {
  const match = options.find((o) => o.value === current);
  if (!match) return options[0]?.label ?? "All";
  return match.count !== undefined
    ? `${match.label} · ${formatStatusCount(match.count)}`
    : match.label;
};

export const hasActiveFilters = (input: {
  selectedCategories: string[];
  showNewestOnly: boolean;
  showVariantsOnly?: boolean;
  hideCashback?: boolean;
  showHotDeals?: boolean;
  showPennyItems?: boolean;
  minStorePrice?: number;
  maxStorePrice?: number;
  minMsrp?: number;
  maxMsrp?: number;
  minDiscount?: number;
}) => {
  return (
    input.selectedCategories.length > 0 ||
    input.showNewestOnly ||
    input.showVariantsOnly ||
    input.hideCashback === false ||
    input.showHotDeals ||
    input.showPennyItems ||
    input.minStorePrice !== undefined ||
    input.maxStorePrice !== undefined ||
    input.minMsrp !== undefined ||
    input.maxMsrp !== undefined ||
    input.minDiscount !== undefined
  );
};
